import styled, { keyframes } from 'styled-components'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Hero from '../components/Hero'
import DeliverySection from '../components/DeliverySection'
import colors from '../utils/style/colors'
import { stockMeta } from '../utils/stockStatus'
import productsData from '../produits/produits.json'
import { supabase } from '../supabase'

// ─── ANIMATIONS ───
const truckSlide = keyframes`
  0%, 100% { transform: translateX(-18px); }
  50%       { transform: translateX(18px); }
`

// ─── STATS ───
const StatsBar = styled.section`
  background: ${colors.vertOlive};
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute; inset: 0;
    background: repeating-linear-gradient(90deg, transparent, transparent 24%, rgba(255,255,255,0.03) 24%, rgba(255,255,255,0.03) 25%);
  }
  @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
`
const StatItem = styled.div`
  text-align: center;
  padding: 40px 20px;
  border-right: 1px solid rgba(255,255,255,0.1);
  position: relative; z-index: 1;
  &:last-child { border-right: none; }
  opacity: 0; transform: translateY(20px);
  transition: opacity 0.6s ${p => p.$delay || '0s'}, transform 0.6s ${p => p.$delay || '0s'};
  &.visible { opacity: 1; transform: translateY(0); }
  @media (max-width: 768px) { border-right: none; border-bottom: 1px solid rgba(255,255,255,0.1); padding: 28px 16px; }
`
const StatNum = styled.div`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 3rem; font-weight: 900;
  color: ${colors.orClair}; line-height: 1; margin-bottom: 8px;
`
const StatLabel = styled.div`
  font-size: 0.75rem; color: rgba(255,255,255,0.7);
  font-weight: 500; letter-spacing: 0.1em;
  text-transform: uppercase; font-family: ${colors.font};
`

// ─── HELPERS ───
const SectionTag = styled.span`
  display: inline-block;
  color: ${colors.or}; font-size: 0.72rem; font-weight: 600;
  letter-spacing: 0.18em; text-transform: uppercase;
  font-family: ${colors.font};
  border-bottom: 2px solid ${colors.or}; padding-bottom: 4px;
`
const SectionTitle = styled.h2`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(2rem, 3.5vw, 3.2rem); font-weight: 700;
  color: ${colors.texte}; line-height: 1.12; margin-bottom: 14px;
`
const SectionSub = styled.p`
  font-size: 1rem; color: ${colors.texteLeger};
  max-width: 520px; line-height: 1.65; margin-bottom: 56px;
  font-family: ${colors.font};
`

// ─── SECTION PRODUITS ───
const ProduitsSection = styled.section`
  padding: 100px 48px;
  background: ${colors.blanc};
  @media (max-width: 768px) { padding: 64px 20px; }
`
const ProduitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 28px;
  @media (max-width: 960px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 580px) { grid-template-columns: 1fr; }
`

/* Carte produit — exactement le style HTML */
const ProduitCard = styled(Link)`
  border: 1px solid ${colors.beigeFonce};
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  text-decoration: none;
  display: block;
  position: relative;
  opacity: 0; transform: translateY(24px);
  transition: opacity 0.5s ${p => p.$delay || '0s'}, transform 0.5s ${p => p.$delay || '0s'},
              box-shadow 0.3s, border-color 0.3s;
  &.visible { opacity: 1; transform: translateY(0); }
  &:hover {
    box-shadow: 0 16px 48px rgba(58,90,46,0.15);
    border-color: ${colors.or};
  }
`
const ProduitImg = styled.div`
  height: 200px;
  display: flex; align-items: center; justify-content: center;
  font-size: 4rem;
  position: relative; overflow: hidden;
  background: ${p =>
    p.$type === 'paille'  ? 'linear-gradient(135deg, rgba(200,168,75,0.13), #e8d5b8)' :
    p.$type === 'foin'    ? 'linear-gradient(135deg, rgba(90,138,69,0.13), #e8f0e4)' :
    'linear-gradient(135deg, rgba(107,76,42,0.13), #f0e8dc)'
  };
  img {
    width: 100%; height: 100%; object-fit: cover;
    position: absolute; inset: 0;
  }
`
const ProduitBadge = styled.span`
  position: absolute; top: 14px; right: 14px;
  background: ${p => p.$bg || colors.vertOlive}; color: #fff;
  font-size: 0.68rem; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase;
  padding: 5px 12px; border-radius: 2px;
  font-family: ${colors.font};
`
const ProduitBody = styled.div`
  padding: 24px;
`
const ProduitName = styled.h3`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.2rem; font-weight: 700;
  margin-bottom: 10px; color: ${colors.texte};
`
const ProduitDesc = styled.p`
  font-size: 0.88rem; color: ${colors.texteLeger};
  line-height: 1.6; margin-bottom: 20px;
  font-family: ${colors.font};
`
const ProduitMeta = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 16px;
`
const MetaTag = styled.span`
  background: ${colors.beige}; color: ${colors.vertOlive};
  font-size: 0.72rem; font-weight: 600;
  padding: 4px 10px; border-radius: 2px;
  font-family: ${colors.font}; letter-spacing: 0.04em;
`
const ProduitLink = styled.span`
  font-size: 0.8rem; font-weight: 600;
  color: ${colors.vertClair}; letter-spacing: 0.06em;
  text-transform: uppercase;
  display: flex; align-items: center; gap: 6px;
  transition: gap 0.2s, color 0.2s;
  font-family: ${colors.font};
  ${ProduitCard}:hover & { gap: 12px; color: ${colors.or}; }
`

const VoirTousBtn = styled(Link)`
  display: inline-flex; align-items: center; gap: 8px;
  margin-top: 48px;
  border: 1.5px solid ${colors.or}; color: ${colors.texte};
  padding: 13px 28px; border-radius: 3px;
  font-family: ${colors.font}; font-size: 0.85rem;
  font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase;
  text-decoration: none; transition: all 0.25s;
  &:hover {
    background: ${colors.or}; transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(200,168,75,0.3);
  }
`

// ─── POURQUOI NOUS ───
const WhySection = styled.section`
  padding: 100px 48px; background: ${colors.beige};
  position: relative; overflow: hidden;
  &::before {
    content: ''; position: absolute;
    top: -100px; right: -100px;
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(200,168,75,0.08) 0%, transparent 70%);
    pointer-events: none;
  }
  @media (max-width: 768px) { padding: 64px 20px; }
`
const WhyGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center;
  @media (max-width: 900px) { grid-template-columns: 1fr; gap: 50px; }
`
const AdvList = styled.div`
  display: flex; flex-direction: column; gap: 24px; margin-top: 38px;
`
const AdvItem = styled.div`
  display: flex; gap: 20px; align-items: flex-start;
  opacity: 0; transform: translateX(-20px);
  transition: opacity 0.5s ${p => p.$delay || '0s'}, transform 0.5s ${p => p.$delay || '0s'};
  &.visible { opacity: 1; transform: translateX(0); }
`
const AdvIcon = styled.div`
  width: 52px; height: 52px; min-width: 52px;
  background: ${colors.vertOlive}; border-radius: 6px;
  display: flex; align-items: center; justify-content: center; font-size: 1.4rem;
`
const AdvTitle = styled.h4`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.05rem; font-weight: 700;
  color: ${colors.texte}; margin-bottom: 6px;
`
const AdvDesc = styled.p`
  font-size: 0.87rem; color: ${colors.texteLeger};
  line-height: 1.65; font-family: ${colors.font};
`
const MapCard = styled.div`
  background: ${colors.vertOlive}; border-radius: 12px;
  padding: 48px 36px;
  display: flex; flex-direction: column; align-items: center; gap: 24px;
  position: relative; overflow: hidden;
  &::before {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(58,90,46,0.3), rgba(107,76,42,0.2));
  }
`
const RouteRow = styled.div`
  display: flex; align-items: center; gap: 20px; position: relative; z-index: 1;
`
const CountryBox = styled.div`
  background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.25);
  border-radius: 8px; padding: 20px 28px; text-align: center;
`
const CountryFlag = styled.div` font-size: 2.6rem; margin-bottom: 8px; `
const CountryName = styled.div`
  color: #fff; font-weight: 600; font-size: 0.82rem;
  letter-spacing: 0.07em; text-transform: uppercase; font-family: ${colors.font};
`
const CountryRole = styled.div`
  color: rgba(255,255,255,0.45); font-size: 0.72rem; margin-top: 4px; font-family: ${colors.font};
`
const RouteArrow = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 8px;
`
const TruckEmoji = styled.div`
  font-size: 1.4rem;
  animation: ${truckSlide} 2.5s ease-in-out infinite;
`
const RouteLine = styled.div`
  width: 80px; height: 2px;
  background: linear-gradient(90deg, rgba(200,168,75,0.3), ${colors.or}, rgba(200,168,75,0.3));
`
const RouteLabel = styled.div`
  color: ${colors.orClair}; font-size: 0.68rem; font-weight: 600;
  letter-spacing: 0.12em; text-transform: uppercase;
  font-family: ${colors.font}; white-space: nowrap;
`
const MapTagline = styled.div`
  color: rgba(255,255,255,0.7); font-size: 0.86rem; line-height: 1.65;
  text-align: center; font-family: ${colors.font}; position: relative; z-index: 1;
  strong { color: ${colors.orClair}; }
`

// ─── TÉMOIGNAGE ───
const TestiSection = styled.section`
  background: ${colors.texte}; padding: 90px 48px;
  text-align: center; position: relative; overflow: hidden;
  &::before {
    content: '❝'; position: absolute;
    top: -30px; left: 50%; transform: translateX(-50%);
    font-size: 18rem; color: rgba(200,168,75,0.04);
    font-family: Georgia, serif; pointer-events: none; line-height: 1;
  }
  @media (max-width: 768px) { padding: 64px 20px; }
`
const TestiInner = styled.div`
  max-width: 780px; margin: 0 auto; position: relative; z-index: 1;
`
const TestiTrack = styled.div`
  position: relative; min-height: 200px;
`
const TestiSlide = styled.div`
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center;
  transition: opacity 0.6s ease;
  opacity: ${p => p.$active ? 1 : 0};
  pointer-events: ${p => p.$active ? 'auto' : 'none'};
  padding-bottom: 8px;
`
const TestiQuote = styled.p`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(1.1rem, 2.3vw, 1.55rem); font-style: italic;
  color: rgba(255,255,255,0.9); line-height: 1.7; margin-bottom: 32px;
  max-width: 680px;
`
const TestiAuthor = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 14px;
`
const AuthorAvatar = styled.div`
  width: 48px; height: 48px; background: ${colors.vertClair};
  border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 1.4rem;
  flex-shrink: 0;
`
const AuthorName = styled.div` color: ${colors.orClair}; font-weight: 700; font-size: 0.95rem; font-family: ${colors.font}; text-align: left; `
const TestiNav = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 44px;
`
const TestiDot = styled.button`
  width: ${p => p.$active ? '28px' : '8px'}; height: 8px;
  border-radius: 4px; border: none; cursor: pointer;
  background: ${p => p.$active ? colors.or : 'rgba(255,255,255,0.2)'};
  transition: all 0.3s; padding: 0;
`
const TestiArrow = styled.button`
  width: 38px; height: 38px; border-radius: 50%; border: 1px solid rgba(200,168,75,0.3);
  background: transparent; color: rgba(255,255,255,0.5); cursor: pointer;
  display: flex; align-items: center; justify-content: center; font-size: 1rem;
  transition: all 0.2s;
  &:hover { border-color: ${colors.or}; color: ${colors.or}; }
`
const TestiSectionLabel = styled.div`
  font-family: ${colors.font}; font-size: 0.7rem; font-weight: 700;
  letter-spacing: 0.2em; text-transform: uppercase;
  color: ${colors.or}; margin-bottom: 48px;
  display: flex; align-items: center; justify-content: center; gap: 12px;
  &::before, &::after { content: ''; flex: 1; max-width: 60px; height: 1px; background: rgba(200,168,75,0.3); }
`

// ─── CTA DEVIS ───
// ─── COMPOSANT ───

function TestiCarousel() {
  const [current, setCurrent] = useState(0)
  const [dir, setDir] = useState('right')
  const [temoignages, setTemoignages] = useState([])
  const timerRef = useRef(null)
  const temoignagesRef = useRef([])
  const currentRef = useRef(0)

  useEffect(() => {
    supabase.from('reviews').select('*').neq('visible', false).order('created_at', { ascending: false })
      .then(({ data }) => {
        const mapped = (data || []).map(a => ({
          quote: a.content,
          nom: a.author,
          role: a.role || '',
          avatar: a.role?.toLowerCase().includes('bovin') ? '🐄'
                : a.role?.toLowerCase().includes('cheval') || a.role?.toLowerCase().includes('haras') ? '🐎'
                : a.role?.toLowerCase().includes('caprin') || a.role?.toLowerCase().includes('chèvre') ? '🐐'
                : '🌾',
        }))
        temoignagesRef.current = mapped
        setTemoignages(mapped)
        setCurrent(0)
        currentRef.current = 0
      })
  }, [])

  const goTo = (idx, direction) => {
    const len = temoignagesRef.current.length
    if (!len) return
    const safeIdx = ((idx % len) + len) % len
    setDir(direction)
    setCurrent(safeIdx)
    currentRef.current = safeIdx
  }

  const next = () => goTo(currentRef.current + 1, 'right')
  const prev = () => goTo(currentRef.current - 1, 'left')

  useEffect(() => {
    if (!temoignages.length) return
    timerRef.current = setInterval(next, 5000)
    return () => clearInterval(timerRef.current)
  }, [temoignages.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const pause = () => clearInterval(timerRef.current)
  const resume = () => {
    if (!temoignages.length) return
    timerRef.current = setInterval(next, 5000)
  }

  if (!temoignages.length) return null

  return (
    <TestiSection onMouseEnter={pause} onMouseLeave={resume}>
      <TestiInner>
        <TestiSectionLabel>Ce que disent nos clients</TestiSectionLabel>
        <TestiTrack style={{ minHeight: '200px', height: temoignages.length ? undefined : '200px' }} ref={el => {
          if (el) {
            const activeSlide = el.querySelector('[data-active="true"]')
            if (activeSlide) el.style.height = activeSlide.scrollHeight + 'px'
          }
        }}>
          {temoignages.map((t, i) => (
            <TestiSlide key={i} $active={i === current} $dir={dir} data-active={i === current}>
              <TestiQuote>"{t.quote}"</TestiQuote>
              <TestiAuthor>
                <AuthorAvatar>{t.avatar}</AuthorAvatar>
                <div>
                <AuthorName>{t.nom}{t.role && <span style={{ color: 'rgba(255,255,255,0.42)', fontWeight: 400, fontSize: '0.78rem', marginLeft: 8 }}>— {t.role}</span>}</AuthorName>
                </div>
              </TestiAuthor>
            </TestiSlide>
          ))}
        </TestiTrack>
        <TestiNav>
          <TestiArrow onClick={prev}>‹</TestiArrow>
          {temoignages.map((_, i) => (
            <TestiDot key={i} $active={i === current} onClick={() => goTo(i, i > current ? 'right' : 'left')} />
          ))}
          <TestiArrow onClick={next}>›</TestiArrow>
        </TestiNav>
      </TestiInner>
    </TestiSection>
  )
}

function Home() {
  const statsRef  = useRef(null)
  const cardsRef  = useRef(null)
  const advRef    = useRef(null)
  const [homeProducts, setHomeProducts] = useState([])

  // Aplatir tous les produits en liste de cartes (fallback JSON)
  const allProducts = productsData.products.flatMap(product =>
    product.types.map(type => ({ product, type }))
  )

  // Fetch produits depuis Supabase (show_on_home + available + images triées)
  useEffect(() => {
    supabase
      .from('products')
      .select('*, product_images(*)')
      .neq('show_on_home', false)
      .order('home_order')
      .then(({ data }) => {
        if (!data) return
        setHomeProducts(data)
      })
  }, [])

  useEffect(() => {
    if (!homeProducts.length) return
    const els = cardsRef.current?.querySelectorAll('[data-card]')
    if (!els) return
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target) } })
    }, { threshold: 0.1 })
    els.forEach(el => io.observe(el))
    return () => io.disconnect()
  }, [homeProducts])

  useEffect(() => {
    const observe = (ref, selector) => {
      if (!ref.current) return
      const els = ref.current.querySelectorAll(selector)
      const io = new IntersectionObserver(entries => {
        entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target) } })
      }, { threshold: 0.15 })
      els.forEach(el => io.observe(el))
      return () => io.disconnect()
    }
    observe(statsRef, '[data-stat]')
    observe(advRef, '[data-adv]')
  }, [])

  return (
    <div>
      {/* 1 — HERO */}
      <Hero />

      {/* 2 — STATS */}
      <StatsBar ref={statsRef}>
        {[
          { num: '10+',  label: "Années d'expérience" },
          { num: '2000T+', label: 'Stocks minimum de marchandises/saison'   },
          { num: '24h',  label: 'Délai de réponse'    },
          { num: '100%', label: 'Qualité garantie'      },
        ].map((s, i) => (
          <StatItem key={s.label} data-stat $delay={`${i * 0.12}s`}>
            <StatNum>{s.num}</StatNum>
            <StatLabel>{s.label}</StatLabel>
          </StatItem>
        ))}
      </StatsBar>

      {/* 3 — PRODUITS */}
      <ProduitsSection id="produits">
        <SectionTag>Nos produits</SectionTag>
        <SectionTitle>Paille & Fourrage<br />de qualité</SectionTitle>
        <SectionSub>
          Sélectionnés directement auprès de producteurs Français en étroite collaboration depuis des années, 
          nos produits respectent les exigences du marché suisse.
        </SectionSub>
        <div ref={cardsRef}>
          <ProduitsGrid>
            {homeProducts.slice(0, 4).map((p, i) => {
              const sortedImgs = [...(p.product_images || [])].sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
              const img = sortedImgs[0]?.url
              const isFoin = p.name?.toLowerCase().includes('foin') || p.name?.toLowerCase().includes('luzerne') || p.name?.toLowerCase().includes('prairie')
              const info = { emoji: isFoin ? '🌿' : '🌾', type: isFoin ? 'foin' : 'paille' }
              const jsonMatch = allProducts.find(({ product }) =>
                p.name?.toLowerCase().includes(product.name.toLowerCase())
              )
              const type = jsonMatch?.type
              return (
                <ProduitCard
                  key={p.id}
                  to={`/produits/${p.id}`}
                  data-card
                  $delay={`${i * 0.15}s`}
                >
                  <ProduitImg $type={info.type}>
                    {img
                      ? <img src={img} alt={p.name} />
                      : <span>{info.emoji}</span>
                    }
                    <ProduitBadge $bg={stockMeta(p).badge}>
                      {stockMeta(p).label}
                    </ProduitBadge>
                  </ProduitImg>
                  <ProduitBody>
                    <ProduitName>{p.name}</ProduitName>
                    <ProduitDesc>{p.description || type?.description || ''}</ProduitDesc>
                    <ProduitMeta>
                      {(p.usages || type?.formats?.[0]?.usage_recommande || []).map(u => (
                        <MetaTag key={u}>{u}</MetaTag>
                      ))}
                    </ProduitMeta>
                    <ProduitLink>Demander un prix <span>→</span></ProduitLink>
                  </ProduitBody>
                </ProduitCard>
              )
            })}
          </ProduitsGrid>
        </div>
        <div style={{ textAlign: 'center' }}>
          <VoirTousBtn to="/produits">Voir tous les produits &nbsp;→</VoirTousBtn>
        </div>
      </ProduitsSection>

      {/* 4 — POURQUOI NOUS */}
      <WhySection id="pourquoi">
        <WhyGrid>
          <div>
            <SectionTag>Pourquoi nous choisir</SectionTag>
            <SectionTitle>L'export France-Suisse,<br />c'est notre spécialité</SectionTitle>
            <div ref={advRef}>
              <AdvList>
                {[
                  { icon: '🏔️', title: 'Livraison directe en Suisse',    desc: "Nous gérons l'intégralité de la logistique, du chargement jusqu'à la livraison chez vous. Formalités douanières incluses.", delay: '0s'    },
                  { icon: '🌾', title: 'Qualité & Poids garanties',  desc: 'Chaque marchandise est contrôlé au chargement. Ticket de pesée fourni systématiquement.',                                         delay: '0.12s' },
                  { icon: '💶', title: 'Prix compétitifs, devis rapide',  desc: 'Réponse sous 24h. Pas de règlement en avance, paiement au déchargement le jour de la livraison.',                                         delay: '0.24s' },
                  { icon: '📱', title: 'Interlocuteur unique & réactif',  desc: 'Un seul contact pour toutes vos commandes, disponible et à votre écoute.',                                                    delay: '0.36s' },
                ].map(a => (
                  <AdvItem key={a.title} data-adv $delay={a.delay}>
                    <AdvIcon>{a.icon}</AdvIcon>
                    <div><AdvTitle>{a.title}</AdvTitle><AdvDesc>{a.desc}</AdvDesc></div>
                  </AdvItem>
                ))}
              </AdvList>
            </div>
          </div>
          <MapCard>
            <RouteRow>
              <CountryBox><CountryFlag>🇫🇷</CountryFlag><CountryName>France</CountryName><CountryRole>Chargement</CountryRole></CountryBox>
              <RouteArrow>
                <TruckEmoji>🚛</TruckEmoji>
                <RouteLine />
                <RouteLabel>Export direct</RouteLabel>
              </RouteArrow>
              <CountryBox><CountryFlag>🇨🇭</CountryFlag><CountryName>Suisse</CountryName><CountryRole>Livraison</CountryRole></CountryBox>
            </RouteRow>
            <MapTagline>
              Livraison dans toute la Suisse —<br />
              <strong>Jura, Vaud, Neuchâtel, Fribourg, Berne</strong><br />
              et tous les autres cantons.
            </MapTagline>
          </MapCard>
        </WhyGrid>
      </WhySection>

      {/* 5 — LIVRAISON */}
      <DeliverySection />

      {/* 6 — TÉMOIGNAGES */}
      <TestiCarousel />


    </div>
  )
}

export default Home
